/**
 * SignInNotice — the one place a returning sign-in reports itself: "Finishing sign-in…" while the
 * Supabase session becomes an account session, or a short, plain reason it did not.
 *
 * A failed redirect cannot be retried from here (the provider's code is spent), so it offers
 * "Sign in again", which opens the sign-in window. Only a failed backend exchange, where the
 * Supabase session is still good, offers "Try again".
 */
import { useState } from 'react';
import { Loader2, TriangleAlert, X } from 'lucide-react';
import type { LoginFailure } from '../../auth/useSupabaseLogin';
import { LoginButton } from './LoginButton';
import { useT } from "../../i18n/i18nContext";

const NOTICE_ICON_SIZE = 18;

interface SignInNoticeProps {
  isSigningIn: boolean;
  failure: LoginFailure | null;
  onRetry: () => void;
  onDismiss: () => void;
}

export function SignInNotice({ isSigningIn, failure, onRetry, onDismiss }: SignInNoticeProps) {
  const t = useT();
  const [dialogOpen, setDialogOpen] = useState(false);

  const openSignIn = () => {
    onDismiss();
    setDialogOpen(true);
  };

  return (
    <>
      {isSigningIn && !failure && (
        <div role="status" className="suikamon-signin-notice">
          <Loader2 size={NOTICE_ICON_SIZE} aria-hidden="true" className="suikamon-signin-notice-spinner" />
          <span className="suikamon-signin-notice-text">{t('account.signInNotice.finishing')}</span>
        </div>
      )}

      {failure && (
        <div role="alert" className="suikamon-signin-notice suikamon-signin-notice--error">
          <TriangleAlert size={NOTICE_ICON_SIZE} aria-hidden="true" className="suikamon-signin-notice-icon" />
          <span className="suikamon-signin-notice-text">{failure.message}</span>
          <button
            type="button"
            className="suikamon-signin-notice-action"
            onClick={failure.retryable ? onRetry : openSignIn}
          >
            {t(failure.retryable ? 'account.signInNotice.retry' : 'account.signInNotice.again')}
          </button>
          <button
            type="button"
            className="suikamon-signin-notice-close"
            onClick={onDismiss}
            aria-label={t('account.signInNotice.dismiss')}
          >
            <X size={NOTICE_ICON_SIZE} aria-hidden="true" />
          </button>
        </div>
      )}

      {dialogOpen && <LoginButton hideTrigger open onOpenChange={setDialogOpen} />}
    </>
  );
}
